import { ApiError } from "../../api/client";
import type { Machine } from "../../api/types";
import { ConfirmDialog } from "../../components/ui/ConfirmDialog";
import { useToast } from "../../components/ui/toast-context";
import { useDeleteMachine } from "./hooks";

interface RemoveMachineDialogProps {
  /** The machine to remove, or null when the dialog is closed. */
  machine: Machine | null;
  onClose: () => void;
}

/**
 * Operator removal (Task 5): purge a single machine from the console. This is
 * the soft-delete path (`?purge=true`) used to clear ghost/stale rows — it does
 * NOT uninstall the agent or export history (that is the separate full-delete
 * dialog). On confirm the row is soft-deleted and its live session dropped; a
 * genuinely-reconnecting machine re-appears on its next check-in.
 */
export function RemoveMachineDialog({
  machine,
  onClose,
}: RemoveMachineDialogProps) {
  const toast = useToast();
  const remove = useDeleteMachine();

  function onConfirm() {
    if (!machine) return;
    remove.mutate(
      { agentId: machine.agent_id, params: { purge: true } },
      {
        onSuccess: () => {
          toast.success(
            "Machine removed",
            `${machine.hostname} was removed from the console.`,
          );
          onClose();
        },
        onError: (err) => {
          toast.error(
            "Could not remove machine",
            err instanceof ApiError
              ? `${err.message}${err.code ? ` (${err.code})` : ""}`
              : "The server did not respond. The machine was not removed.",
          );
        },
      },
    );
  }

  return (
    <ConfirmDialog
      open={machine != null}
      title="Remove machine?"
      danger
      busy={remove.isPending}
      confirmLabel="Remove machine"
      cancelLabel="Keep machine"
      onConfirm={onConfirm}
      onCancel={onClose}
      body={
        machine ? (
          <p style={{ marginTop: 0 }}>
            Remove <strong>{machine.hostname}</strong> from the GuruConnect
            console. Its live session is dropped and the row disappears from the
            list. If this machine is genuinely still in service it re-appears the
            next time its agent checks in.
          </p>
        ) : null
      }
    />
  );
}
